import React, { useEffect, useRef } from 'react';
import { NODE_REGISTRY } from '../config/nodeRegistry';
import { DataType } from '../types';
import clsx from 'clsx';
import { Plus } from 'lucide-react';

interface NodeContextMenuProps {
  x: number;
  y: number;
  sourceType: DataType;
  isInput: boolean;
  onClose: () => void;
  onSelect: (targetNodeType: string) => void;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({ x, y, sourceType, isInput, onClose, onSelect }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Filter compatible nodes
  const compatibleNodes = Object.values(NODE_REGISTRY).filter(nodeDef => {
    if (isInput) {
      // If we clicked an input, we are looking for nodes that output this type
      return nodeDef.outputs.some(output => output.type === sourceType);
    } else {
      // If we clicked an output, we are looking for nodes that input this type
      return nodeDef.inputs.some(input => input.type === sourceType);
    }
  });

  return (
    <div 
      ref={menuRef}
      className="fixed z-[100] bg-slate-800 border border-slate-600 rounded-lg shadow-2xl py-1 min-w-[160px] animate-in fade-in zoom-in-95 duration-100"
      style={{ left: x, top: y }}
    >
      <div className="px-3 py-1.5 border-b border-slate-700 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
        {isInput ? 'Connect from...' : 'Connect to...'}
      </div>
      
      {compatibleNodes.length === 0 ? (
        <div className="px-3 py-2 text-xs text-slate-500 italic">
          No compatible nodes
        </div>
      ) : (
        compatibleNodes.map((node) => (
          <button
            key={node.type}
            onClick={() => onSelect(node.type)}
            className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 hover:text-white transition flex items-center gap-2 group"
          >
            <div className={clsx(
              "w-1.5 h-1.5 rounded-full transition",
              isInput ? "bg-green-400" : "bg-blue-400"
            )} />
            {node.label}
          </button>
        ))
      )}
    </div>
  );
};

export default NodeContextMenu;